import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Router } from '@angular/router';
import { of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs';
import { SearchService } from '../search.service';
import { SearchResult } from '../searchResult';

const EMPTY_RESULT: SearchResult = {
  accounts: [],
  jobs: [],
  orders: [],
  contacts: [],
};

@Component({
  selector: 'app-widget-search-bar',
  templateUrl: './widget-search-bar.component.html',
  styleUrls: ['./widget-search-bar.component.css'],
  imports: [ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetSearchBarComponent {
  private searchService = inject(SearchService);
  private router = inject(Router);

  f = new UntypedFormGroup({
    searchInput: new UntypedFormControl(''),
  });

  private term$ = new Subject<string>();

  // Single subscription to the debounced search pipeline, surfaced as a signal
  // so the OnPush view updates automatically when results arrive.
  private readonly searchResults = toSignal(
    this.term$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(term =>
        term.length === 0
          ? of(EMPTY_RESULT)
          : // Recover from a failed search instead of letting the error tear
            // down the whole stream — otherwise one bad search permanently
            // kills type-ahead (results stuck open, later searches dead).
            this.searchService
              .search(term)
              .pipe(catchError(() => of(EMPTY_RESULT))),
      ),
    ),
    { initialValue: EMPTY_RESULT },
  );

  readonly accountResults = computed(() => this.searchResults().accounts);
  readonly jobResults = computed(() => this.searchResults().jobs);
  readonly orderResults = computed(() => this.searchResults().orders);
  readonly contactResults = computed(() => this.searchResults().contacts);
  readonly windowOpen = computed(() => {
    const results = this.searchResults();
    return (
      results.accounts.length > 0 ||
      results.jobs.length > 0 ||
      results.orders.length > 0 ||
      results.contacts.length > 0
    );
  });

  onAccountClick(accountId: string): void {
    this.router.navigate(['/accounts', accountId]);
    this.f.reset();
  }

  onJobClick(jobNumber: string): void {
    this.router.navigate(['/jobs', jobNumber]);
    this.f.reset();
  }

  onOrderClick(orderId: string): void {
    this.router.navigate(['/orders', orderId]);
    this.f.reset();
  }

  onContactClick(contactId: string): void {
    this.router.navigate(['/contacts', contactId]);
    this.f.reset();
  }

  onSearch(): void {
    if (this.f.get('searchInput').value) {
      // TODO search results page
    } else {
      this.term$.next('');
    }
  }

  onBlur(): void {
    this.term$.next('');
  }

  onKeyUp(): void {
    this.term$.next(this.f.get('searchInput').value);
  }
}
